import React from 'react';

/**
 * ⏳🎬 EventStripBar (09-23, MOVIE_NIGHT_UX_HANDOFF §3.1, Nina's ruling 7: the
 * countdown lives at the TOP only). The composer's countdown line
 * (HumanInputForm, the `eventStrip` block) lifted into ONE pure component so
 * the same markup mounts in three places and never twice at once (the 07-20
 * mobile double-banner lesson, "One line, never floating" retired):
 *   · desktop = in flow, the first row of the identity block, centered
 *   · phone   = a fixed pill under the island (TOAST_CENTERED centering)
 *   · sheet   = top-center of the cinema sheet, no CTA, dimmed while playing
 *
 * `strip` = utils/calendarView.eventStrip(): { key, phase, emoji, text, clock,
 * cta, go, room, hot }. The page ticks it and owns the tap (onGo gets the
 * strip) and the ✕ store (onHide gets `${key}|${phase}`, the value the
 * hif_event_strip_hidden key keeps). ✕ renders ONLY in before / opening /
 * today: the doors and the film are un-hideable. `hidden` = a top toast is up
 * or L1/L2 carries the clock (the pill YIELDS, never stacks).
 *
 * data-event-strip / data-event-go / data-event-clock are bundle grep markers:
 * keep them byte-for-byte.
 * ⚠️ phone: left/right 0 + auto margins + fit-content (the page's
 * TOAST_CENTERED), NEVER left:50% + translateX (the box collapses to one word
 * per line at 390px). ⚠️ No global box-sizing in this frontend.
 */
export const HIDEABLE_PHASES = ['before', 'opening', 'today'];
export const hideKeyOf = (strip) => (strip ? `${strip.key}|${strip.phase}` : '');

const TEXT = '#f3d9a6';
const CLOCK_HOT = '#ffd98a';
const CLOCK = '#e9b565';

// `solid` (desktop, 09-25): a room painting is the wallpaper and the title
// scrim has stepped aside, so the translucent pill would vanish over bright
// art (measured on the sun room by day): it takes the phone pill's dark fill.
export default function EventStripBar({ strip, onGo = null, onHide = null, variant = 'desktop', hidden = false, dim = false, solid = false }) {
  if (hidden || !strip || !strip.text) return null;
  const phone = variant === 'phone';
  const sheet = variant === 'sheet';
  const hot = !!strip.hot;
  const hideable = HIDEABLE_PHASES.includes(strip.phase) && typeof onHide === 'function';

  const look = {
    display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, boxSizing: 'border-box',
    border: `1px solid ${hot ? 'rgba(233,181,101,0.75)' : 'rgba(233,181,101,0.35)'}`,
    color: TEXT, lineHeight: 1.25, fontSize: 13,
    opacity: dim ? 0.55 : 1, transition: 'opacity 0.3s ease',
  };
  const layout = phone
    ? {
      // 📱 the pill: under the island, clear of the Back circle (left 10) and
      // the ♫ FAB (right 10), over the TV/Locals layers (4600), under the dock
      // (5000) / nav (5100) / peek (5200).
      position: 'fixed', top: 'calc(8px + env(safe-area-inset-top, 0px))',
      left: 0, right: 0, marginLeft: 'auto', marginRight: 'auto', width: 'fit-content',
      maxWidth: 'calc(100vw - 120px)', zIndex: 4700, height: 38, padding: '0 8px 0 12px', borderRadius: 999,
      background: hot ? 'rgba(46,34,18,0.96)' : 'rgba(38,29,16,0.94)',
      boxShadow: '0 4px 14px rgba(0,0,0,0.45)',
    }
    : {
      // 🖥️ in flow: the root never scrolls, so it simply persists; hugs its
      // text, centered, the title ellipsizes once the cap binds.
      margin: '0 auto', width: 'fit-content', maxWidth: 'min(100vw - 480px, 640px)',
      height: 30, padding: '0 6px 0 10px', borderRadius: 8,
      background: solid
        ? (hot ? 'rgba(46,34,18,0.96)' : 'rgba(38,29,16,0.94)')
        : (hot ? 'rgba(233,181,101,0.14)' : 'rgba(233,181,101,0.07)'),
    };

  return (
    <div data-event-strip={strip.phase} data-event-strip-variant={variant} role="status" style={{ ...look, ...layout }}>
      <span aria-hidden="true" style={{ flexShrink: 0 }}>{strip.emoji}</span>
      <span style={{ flex: '1 1 auto', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 600 }}>{strip.text}</span>
      {strip.clock && (
        <span data-event-clock="1" style={{ flexShrink: 0, fontVariantNumeric: 'tabular-nums', fontWeight: 800, color: hot ? CLOCK_HOT : CLOCK, whiteSpace: 'nowrap' }}>{strip.clock}</span>
      )}
      {!sheet && (
        <button type="button" data-event-go={strip.go}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => { if (onGo) onGo(strip); }}
          style={{ flexShrink: 0, font: 'inherit', border: '1px solid rgba(233,181,101,0.6)', background: 'rgba(233,181,101,0.16)', color: CLOCK_HOT, borderRadius: 6, padding: phone ? '4px 10px' : '2px 8px', fontSize: 12.5, fontWeight: 800, cursor: 'pointer', whiteSpace: 'nowrap', lineHeight: 1.2 }}>
          {strip.cta}
        </button>
      )}
      {hideable && (
        <button type="button" aria-label="Hide until this changes" title="Hide until this changes"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => onHide(hideKeyOf(strip))}
          style={{ flexShrink: 0, border: 'none', background: 'transparent', color: 'rgba(243,217,166,0.6)', cursor: 'pointer', padding: phone ? '6px 4px' : '0 2px', fontSize: 13, lineHeight: 1 }}>✕</button>
      )}
    </div>
  );
}
